<script setup>
// MemoryPopover — shared popover UI for both:
//   - Save to memory (kind="memory") — invoked from MessageMeta's bookmark icon
//   - AI improvement comment (kind="correction") — invoked from AI Draft's
//     Comment button
//
// Teleported to <body> so it can't be clipped by parent overflow:hidden or
// trapped behind a higher-stacking-context sibling (Chatwoot's sticky reply
// box). Position is measured from an anchor span left in the original DOM
// location, so the popover still appears next to its trigger.
//
// Uses Chatwoot theme tokens (n-solid-1, n-alpha-1, n-slate-11/12, n-brand)
// so colors adapt to dark/light mode.
import { ref, watch, nextTick, onUnmounted } from 'vue';
import { activePopoverId, nextPopoverId } from './popoverSingleton';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: 'Save to memory' },
  placeholder: {
    type: String,
    default:
      '(Optional) Why is this useful? e.g. Shopee shipping policy: free above RM50 east MY',
  },
  // Anchor edge: 'right' anchors right-edge to trigger, 'left' anchors left-edge.
  anchor: { type: String, default: 'right' },
  // saving|saved|error|idle — controlled by parent
  state: { type: String, default: 'idle' },
});
const emit = defineEmits(['update:modelValue', 'submit']);

const note = ref('');
const scope = ref('auto');
const anchorEl = ref(null); // sentinel span left in the original DOM
const popStyle = ref({ top: '0px', left: '0px' });
const myId = nextPopoverId();

const SCOPES = [
  { v: 'auto', l: 'Auto' },
  { v: 'all', l: 'All' },
  { v: 'shopee', l: 'Shopee' },
  { v: 'tiktok', l: 'TikTok' },
  { v: 'lazada', l: 'Lazada' },
  { v: 'whatsapp', l: 'WA' },
  { v: 'woocommerce', l: 'Woo' },
  { v: 'pos', l: 'POS' },
];

const POPOVER_WIDTH = 288; // matches w-72 (18rem)
const GAP = 4;

function reposition() {
  if (!anchorEl.value) return;
  // Find the trigger element — anchor's previous sibling button OR its
  // parent's first interactive child.
  const parent = anchorEl.value.parentElement;
  const trigger = parent?.querySelector('button') || parent;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const top = rect.bottom + GAP;
  let left;
  if (props.anchor === 'right') {
    // Align popover's right edge with trigger's right edge
    left = rect.right - POPOVER_WIDTH;
  } else {
    left = rect.left;
  }
  // Keep within viewport
  left = Math.max(8, Math.min(left, window.innerWidth - POPOVER_WIDTH - 8));
  popStyle.value = { top: `${top}px`, left: `${left}px` };
}

function onScrollOrResize() {
  if (props.modelValue) reposition();
}

watch(
  () => props.modelValue,
  opened => {
    if (opened) {
      // Claim the singleton — any other open popover sees activePopoverId
      // change and closes itself via the watcher below.
      activePopoverId.value = myId;
      note.value = '';
      scope.value = 'auto';
      nextTick(reposition);
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
    } else {
      if (activePopoverId.value === myId) activePopoverId.value = null;
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    }
  }
);

// If another popover claims the singleton while we're open, close ourselves.
watch(activePopoverId, id => {
  if (props.modelValue && id !== myId) {
    emit('update:modelValue', false);
  }
});

onUnmounted(() => {
  if (activePopoverId.value === myId) activePopoverId.value = null;
  window.removeEventListener('scroll', onScrollOrResize, true);
  window.removeEventListener('resize', onScrollOrResize);
});

function cancel() {
  emit('update:modelValue', false);
}
function submit() {
  emit('submit', { note: note.value.trim(), scope: scope.value });
}
</script>

<template>
  <!-- Sentinel span stays in original DOM location so we can measure where
       the trigger sits. Using `display:contents` keeps it layout-neutral. -->
  <span ref="anchorEl" style="display: contents" />
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed z-[1000] w-72 p-2.5 rounded-lg shadow-lg flex flex-col gap-2 text-xs bg-n-solid-1 border border-n-weak"
      :style="popStyle"
      @click.stop
    >
      <div class="font-medium text-n-slate-12">{{ title }}</div>
      <textarea
        v-model="note"
        rows="2"
        :placeholder="placeholder"
        class="w-full px-2 py-1.5 text-xs rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 resize-y focus:outline-none focus:ring-1 focus:ring-n-brand"
      />
      <div class="flex flex-wrap items-center gap-1">
        <span class="text-n-slate-11 mr-1">Apply to:</span>
        <label
          v-for="opt in SCOPES"
          :key="opt.v"
          class="cursor-pointer px-1.5 py-0.5 rounded border transition-colors"
          :class="
            scope === opt.v
              ? 'border-n-brand bg-n-brand/10 text-n-brand font-medium'
              : 'border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-7'
          "
        >
          <input v-model="scope" type="radio" :value="opt.v" class="sr-only" />
          {{ opt.l }}
        </label>
      </div>
      <div class="flex items-center gap-2 justify-end mt-0.5">
        <span v-if="state === 'saving'" class="text-n-slate-11">Saving...</span>
        <span v-else-if="state === 'saved'"
class="text-r-emerald-11"
          >✅ Saved</span>
        <span v-else-if="state === 'error'"
class="text-r-ruby-11"
          >Error — retry</span>
        <button
          v-if="state !== 'saving' && state !== 'saved'"
          type="button"
          class="px-2.5 py-1 rounded-md font-medium text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3 cursor-pointer"
          @click="cancel"
        >
          Cancel
        </button>
        <button
          v-if="state !== 'saving' && state !== 'saved'"
          type="button"
          class="px-2.5 py-1 rounded-md font-medium text-white bg-n-brand hover:bg-n-brand/90 cursor-pointer"
          @click="submit"
        >
          Save
        </button>
      </div>
    </div>
  </Teleport>
</template>
